import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, X, Heart, Clock, Headphones } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PodcastPlayer = ({ podcast, onClose, isMinimized, onToggleMinimize }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(podcast?.duration || 0);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  useEffect(() => {
    // Record play when podcast starts
    if (isPlaying && podcast?.podcast_id) {
      axios.post(`${API_URL}/podcasts/${podcast.podcast_id}/play`).catch(() => {});
    }
  }, [isPlaying, podcast?.podcast_id]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (value) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value[0];
      setCurrentTime(value[0]);
    }
  };

  const skip = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + seconds));
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const toggleLike = async () => {
    try {
      if (liked) {
        await axios.delete(`${API_URL}/podcasts/${podcast.podcast_id}/like`, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/podcasts/${podcast.podcast_id}/like`, {}, { withCredentials: true });
      }
      setLiked(!liked);
    } catch (error) {
      // Already liked/unliked
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!podcast) return null;

  // Minimized player (sidebar)
  if (isMinimized) {
    return (
      <motion.div
        initial={{ x: 100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 100, opacity: 0 }}
        className="fixed right-4 bottom-24 w-72 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden z-40"
        data-testid="podcast-player-mini"
      >
        <audio
          ref={audioRef}
          src={podcast.audio_url}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={() => setIsPlaying(false)}
        />
        
        {/* Mini header */}
        <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-500">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Headphones className="w-4 h-4 text-white flex-shrink-0" />
              <span className="text-white text-sm font-semibold truncate">{podcast.title}</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={onToggleMinimize}
                className="p-1 text-white/80 hover:text-white"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 14h6v6" /><path d="M20 10h-6V4" /><path d="M14 10l7-7" /><path d="M3 21l7-7" />
                </svg>
              </button>
              <button
                onClick={onClose}
                className="p-1 text-white/80 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="px-3 py-2">
          <Slider
            value={[currentTime]}
            max={duration || 100}
            step={1}
            onValueChange={handleSeek}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
        
        {/* Controls */}
        <div className="px-3 pb-3 flex items-center justify-center gap-2">
          <button onClick={() => skip(-10)} className="p-2 text-gray-600 dark:text-gray-300 hover:text-primary">
            <SkipBack className="w-4 h-4" />
          </button>
          <button
            onClick={togglePlay}
            className="p-3 bg-primary rounded-full text-white hover:bg-primary-700 transition-colors"
            data-testid="podcast-play-btn"
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
          </button>
          <button onClick={() => skip(10)} className="p-2 text-gray-600 dark:text-gray-300 hover:text-primary">
            <SkipForward className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    );
  }

  // Full player
  return (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      className="fixed right-4 bottom-24 w-80 bg-white dark:bg-gray-800 rounded-3xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden z-40"
      data-testid="podcast-player-full"
    >
      <audio
        ref={audioRef}
        src={podcast.audio_url}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={() => setIsPlaying(false)}
      />
      
      {/* Header */}
      <div className="relative">
        <div className="h-32 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400">
          {podcast.thumbnail_url && (
            <img src={podcast.thumbnail_url} alt="" className="w-full h-full object-cover opacity-30" />
          )}
        </div>
        <div className="absolute top-2 right-2 flex gap-1">
          <button
            onClick={onToggleMinimize}
            className="p-2 bg-black/20 rounded-full text-white hover:bg-black/40 transition-colors"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 3v3a2 2 0 0 1-2 2H3" /><path d="M21 8h-3a2 2 0 0 1-2-2V3" /><path d="M3 16h3a2 2 0 0 1 2 2v3" /><path d="M16 21v-3a2 2 0 0 1 2-2h3" />
            </svg>
          </button>
          <button
            onClick={onClose}
            className="p-2 bg-black/20 rounded-full text-white hover:bg-black/40 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="absolute -bottom-10 left-4">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 shadow-xl flex items-center justify-center">
            {podcast.thumbnail_url ? (
              <img src={podcast.thumbnail_url} alt="" className="w-full h-full object-cover rounded-2xl" />
            ) : (
              <Headphones className="w-8 h-8 text-white" />
            )}
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="pt-12 px-4 pb-4">
        <h3 className="font-bold text-gray-900 dark:text-white text-lg truncate" data-testid="podcast-title">
          {podcast.title}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
          {podcast.user?.name || 'Unknown Creator'}
        </p>
        {podcast.episode_number && (
          <p className="text-xs text-gray-400 mt-1">
            {podcast.season_number && `Season ${podcast.season_number} • `}Episode {podcast.episode_number}
          </p>
        )}
        
        {/* Progress */}
        <div className="mt-4">
          <Slider
            value={[currentTime]}
            max={duration || 100}
            step={1}
            onValueChange={handleSeek}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>
        
        {/* Controls */}
        <div className="flex items-center justify-center gap-4 mt-4">
          <button
            onClick={() => skip(-15)}
            className="p-2 text-gray-600 dark:text-gray-300 hover:text-primary transition-colors"
          >
            <SkipBack className="w-5 h-5" />
          </button>
          <button
            onClick={togglePlay}
            className="p-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-white shadow-lg hover:shadow-xl transition-all hover:scale-105"
            data-testid="podcast-play-btn"
          >
            {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-0.5" />}
          </button>
          <button
            onClick={() => skip(15)}
            className="p-2 text-gray-600 dark:text-gray-300 hover:text-primary transition-colors"
          >
            <SkipForward className="w-5 h-5" />
          </button>
        </div>
        
        {/* Bottom controls */}
        <div className="flex items-center justify-between mt-4">
          <button
            onClick={toggleLike}
            className={`p-2 rounded-full transition-colors ${liked ? 'text-red-500' : 'text-gray-500 hover:text-red-500'}`}
          >
            <Heart className={`w-5 h-5 ${liked ? 'fill-current' : ''}`} />
          </button>
          
          <div className="flex items-center gap-2">
            <button onClick={toggleMute} className="p-1 text-gray-500 hover:text-primary">
              {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <Slider
              value={[isMuted ? 0 : volume * 100]}
              max={100}
              step={1}
              onValueChange={(value) => setVolume(value[0] / 100)}
              className="w-20"
            />
          </div>
          
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Headphones className="w-3 h-3" />
            {podcast.plays_count || 0}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default PodcastPlayer;
